import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faDownload } from "@fortawesome/free-solid-svg-icons";
import {
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Report } from "../reports/vouchers_report/Subscriptions_report";
import CustomModal from "../../../base-components/CustomModal";
import Popup from "../../../base-components/Popup";
import TablePaginationActions from "../../../base-components/TablePaginationActions";
import dayjs from "dayjs";
import {
  PageHeader,
  StatCard,
  EmptyState,
  TableShell,
  StatusChip,
  initials,
} from "../../../ui";
import SubscriptionStatsModal from "./StatsModal";
import BarChartIcon from "@mui/icons-material/BarChart";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewListIcon from "@mui/icons-material/ViewList";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { motion, useReducedMotion } from "motion/react";
import { getSessionUser, hasPermission, isDynamicStaffRole } from "../../../utils/roleAccess";
import { encodedImagesURI } from "../../../utils/helpers";
import defaultUser from "../../../images/user_default.jpg";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const controlClass =
  "border border-gx-border bg-gx-surface rounded-gx-control py-2.5 px-3 outline-0 w-full text-sm text-gx-ink min-h-[44px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gx-red";

function formatMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return `${value ?? 0} Rs`;
  return `Rs ${n.toLocaleString("en-US")}`;
}

function formatMaybeDate(value) {
  if (!value) return "—";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("MMM DD, YYYY") : "—";
}

function memberCode(ledger) {
  return `${ledger?.location_id?.member_id_temp ?? ""}${ledger?.member_id?.member_id ?? ""}`;
}

function packageCode(ledger) {
  if (!ledger?.package_id) return "—";
  return `${ledger.package_id?.package_id_temp ?? ""}${ledger?.member_id?.member_id ?? ""}`;
}

function remainingDays(endDate) {
  if (!endDate) return null;
  const parsed = dayjs(endDate);
  if (!parsed.isValid()) return null;
  return parsed.startOf("day").diff(dayjs().startOf("day"), "day");
}

function remainingCopy(endDate, isActive) {
  const days = remainingDays(endDate);
  if (days === null) return { label: "No end date", tone: "neutral", days: null };
  if (days < 0) {
    return {
      label: `${Math.abs(days)}d overdue`,
      tone: "danger",
      days,
    };
  }
  if (days === 0) return { label: "Ends today", tone: "warning", days };
  if (days <= 7) return { label: `${days}d left`, tone: "warning", days };
  if (!isActive) return { label: `${days}d left`, tone: "neutral", days };
  return { label: `${days}d left`, tone: "success", days };
}

function cycleProgress(ledger) {
  const start = ledger?.renewed_date || ledger?.start_date;
  const end = ledger?.end_date;
  if (!start || !end) return 0;
  const startDay = dayjs(start);
  const endDay = dayjs(end);
  const total = endDay.diff(startDay, "day");
  if (total <= 0) return ledger?.is_active ? 100 : 0;
  const elapsed = dayjs().diff(startDay, "day");
  return Math.min(100, Math.max(0, (elapsed / total) * 100));
}

function matchesSearch(ledger, search) {
  const q = (search || "").trim().toLowerCase();
  if (!q) return true;
  const combinedMemberID = memberCode(ledger).toLowerCase();
  const combinedPackageID = packageCode(ledger).toLowerCase();
  return (
    (ledger.renewal_frequency || "").toLowerCase().includes(q) ||
    combinedMemberID.includes(q) ||
    combinedPackageID.includes(q) ||
    (ledger.member_id?.name || "").toLowerCase().includes(q) ||
    (ledger.package_id?.title || "").toLowerCase().includes(q)
  );
}

function MemberAvatar({ ledger }) {
  const name = ledger?.member_id?.name || "Member";
  const photoSrc = encodedImagesURI(ledger?.member_id?.photo);
  return (
    <div className="w-12 h-12 rounded-gx overflow-hidden border border-gx-border shrink-0 bg-gx-canvas">
      {photoSrc ? (
        <img
          src={photoSrc}
          alt=""
          className="w-full h-full object-cover"
          onError={(e) => {
            e.currentTarget.src = defaultUser;
          }}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-sm font-semibold text-gx-navy bg-gx-navy/10">
          {initials(name)}
        </div>
      )}
    </div>
  );
}

function RemainingMeter({ ledger }) {
  const meta = remainingCopy(ledger?.end_date, ledger?.is_active);
  const progress = cycleProgress(ledger);
  const barColor =
    meta.tone === "danger"
      ? "#D71920"
      : meta.tone === "warning"
        ? "#F7931E"
        : "#047857";
  return (
    <div className="min-w-[7.5rem]">
      <p className="text-sm font-semibold text-gx-ink">{meta.label}</p>
      <div className="mt-1.5 h-1.5 rounded-full bg-gx-border overflow-hidden">
        <div
          className="h-full rounded-full transition-[width] duration-300"
          style={{ width: `${progress}%`, backgroundColor: barColor }}
        />
      </div>
    </div>
  );
}

function SubscriptionsSkeleton() {
  return (
    <div className="space-y-4" aria-busy="true" aria-live="polite">
      <span className="sr-only">Loading subscriptions</span>
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={`kpi-${i}`} className="gx-glass rounded-gx px-5 py-5 min-h-[132px]">
            <div className="h-3 w-24 rounded-gx bg-gx-border/70 animate-pulse" />
            <div className="h-10 w-20 rounded-gx bg-gx-border/70 animate-pulse mt-6" />
          </div>
        ))}
      </div>
      <div className="gx-glass rounded-gx min-h-[140px] p-5">
        <div className="h-4 w-40 rounded-gx bg-gx-border/70 animate-pulse" />
        <div className="h-11 w-full rounded-gx bg-gx-border/70 animate-pulse mt-4" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={`card-${i}`}
            className="rounded-gx border border-gx-border bg-gx-surface min-h-[180px] p-4"
          >
            <div className="flex gap-3">
              <div className="w-12 h-12 rounded-gx bg-gx-border/70 animate-pulse" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-32 rounded-gx bg-gx-border/70 animate-pulse" />
                <div className="h-3 w-20 rounded-gx bg-gx-border/70 animate-pulse" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Ledgers() {
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();
  const [ledgers, setLedgers] = useState([]);
  const [ledgerToDelete, setLedgerToDelete] = useState("");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [isSuccessAlert, setIsSuccessAlert] = useState(false);
  const [isFaliureAlert, setIsFailureAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [failureMessage, setFailureMessage] = useState("");
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState("");
  const [location_id, setLocationID] = useState("");
  const [company_id, setCompanyID] = useState("");
  const [member_id, setMemberID] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [status, setStatus] = useState("");
  const [balance, setBalance] = useState("");
  const [subscriptionType, setSubscriptionType] = useState("");
  const [quickFilter, setQuickFilter] = useState("");
  const [layout, setLayout] = useState("cards");
  const [openStatsModal, setOpenStatsModal] = useState(false);

  useEffect(() => {
    const sessionStr = sessionStorage.getItem("user-info");
    if (sessionStr) {
      const session = JSON.parse(sessionStr);
      setRole(session.user.role);
      setCompanyID(session.user.company_id);
      setLocationID(session.user.location_id);
      setMemberID(session.user.member_id);
    } else {
      navigate("/login");
    }
  }, [navigate]);

  useEffect(() => {
    const fetchLedgers = async () => {
      if (!role) return;
      setLoading(true);
      await getLedgers();
      setLoading(false);
    };
    fetchLedgers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  async function getLedgers() {
    let endpoint = "";
    if (role === "SuperAdmin") {
      endpoint = "subscription/getAllVouchers";
    } else if (role === "Admin") {
      endpoint = `subscription/get-subscriptions-by-company-id/${company_id}`;
    } else if (role === "ClubAdmin") {
      endpoint = `subscription/get-subscriptions-by-location-id/${location_id}`;
    } else if (isDynamicStaffRole(role)) {
      endpoint = `subscription/get-subscriptions-by-location-id/${location_id}`;
    } else if (role === "User") {
      endpoint = `subscription/get-subscription-by-member-id/${member_id}`;
    }

    if (!endpoint) {
      setLedgers([]);
      return;
    }

    await fetch(BASE_URL + endpoint)
      .then((response) => response.json())
      .then((data) => {
        setLedgers(data.subscriptions || []);
      })
      .catch((error) => {
        console.log(error);
        setLedgers([]);
      });
  }

  async function deleteLedger() {
    const requestOptions = {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
    };
    await fetch(
      BASE_URL + `subscription/delete-subscription/${ledgerToDelete}`,
      requestOptions
    )
      .then((response) => response.json())
      .then(() => {
        getLedgers();
        setIsDeleteModalOpen(false);
        setIsFailureAlert(false);
        setIsSuccessAlert(true);
        setSuccessMessage("Ledger deleted Successfully");
        setIsPopupOpen(true);
        setTimeout(() => {
          setIsPopupOpen(false);
        }, 5000);
      })
      .catch((error) => {
        console.log(error);
        setIsFailureAlert(true);
        setIsSuccessAlert(false);
        setFailureMessage("Failed to delete Ledger");
        setIsPopupOpen(true);
        setTimeout(() => {
          setIsPopupOpen(false);
        }, 5000);
      });
  }

  function toggleDeleteModal() {
    setIsDeleteModalOpen(!isDeleteModalOpen);
  }

  const scopedLedgers = useMemo(() => {
    let filtered = ledgers || [];
    if (searchQuery) {
      filtered = filtered.filter((ledger) => matchesSearch(ledger, searchQuery));
    }
    if (fromDate && toDate) {
      const startFilterDate = new Date(fromDate);
      startFilterDate.setHours(0, 0, 0, 0);
      const endFilterDate = new Date(toDate);
      endFilterDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter((subscription) => {
        const end = new Date(subscription.end_date);
        return end >= startFilterDate && end <= endFilterDate;
      });
    }
    if (subscriptionType) {
      filtered = filtered.filter(
        (subscription) =>
          subscription.renewal_frequency?.toLowerCase() ===
          subscriptionType.toLowerCase()
      );
    }
    return filtered;
  }, [ledgers, searchQuery, fromDate, toDate, subscriptionType]);

  const filtered_ledgers = useMemo(() => {
    let filtered = scopedLedgers;
    if (status) {
      filtered = filtered.filter(
        (subscription) => subscription.is_active === (status === "active")
      );
    }
    if (quickFilter === "expiring") {
      filtered = filtered.filter((subscription) => {
        if (!subscription.is_active) return false;
        const days = remainingDays(subscription.end_date);
        return days !== null && days >= 0 && days <= 7;
      });
    }
    if (quickFilter === "outstanding") {
      filtered = filtered.filter(
        (subscription) => Number(subscription?.member_id?.balance) > 0
      );
    }
    return filtered;
  }, [scopedLedgers, status, quickFilter]);

  const kpis = useMemo(() => {
    const list = scopedLedgers || [];
    const active = list.filter((sub) => sub.is_active).length;
    const expired = list.length - active;
    const expiring = list.filter((sub) => {
      if (!sub.is_active) return false;
      const days = remainingDays(sub.end_date);
      return days !== null && days >= 0 && days <= 7;
    }).length;
    const outstanding = list.reduce(
      (sum, sub) => sum + (Number(sub?.member_id?.balance) || 0),
      0
    );
    return { total: list.length, active, expired, expiring, outstanding };
  }, [scopedLedgers]);

  const paginatedLedgers =
    !filtered_ledgers || filtered_ledgers.length === 0
      ? []
      : rowsPerPage === -1
        ? filtered_ledgers
        : filtered_ledgers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleDownload = () => {
    Report(filtered_ledgers);
  };

  const handleSearch = (search) => {
    setSearchQuery(search);
    setPage(0);
  };

  const handleFilter = () => {
    setPage(0);
  };

  const handleClear = () => {
    setSearchQuery("");
    setFromDate("");
    setToDate("");
    setStatus("");
    setBalance("");
    setSubscriptionType("");
    setQuickFilter("");
    setPage(0);
  };

  const selectKpi = (key) => {
    if (key === "active") {
      setStatus((prev) => (prev === "active" && !quickFilter ? "" : "active"));
      setQuickFilter("");
    } else if (key === "expired") {
      setStatus((prev) => (prev === "pending" && !quickFilter ? "" : "pending"));
      setQuickFilter("");
    } else if (key === "expiring") {
      setQuickFilter((prev) => (prev === "expiring" ? "" : "expiring"));
      setStatus("active");
    } else if (key === "outstanding") {
      setQuickFilter((prev) => (prev === "outstanding" ? "" : "outstanding"));
      setStatus("");
    }
    setPage(0);
  };

  const openVouchers = (id) => {
    navigate(`/vouchers/${id}`);
  };
  const openTransactions = (id) => {
    navigate(`/transactions?subscription_id=${id}`);
  };
  const openMember = (ledger) => {
    if (!ledger?.member_id) return;
    navigate("/profilemember", {
      state: { id: ledger.member_id, prevPath: "/ledgers" },
    });
  };

  const filtersActive = Boolean(
    searchQuery || fromDate || toDate || status || subscriptionType || quickFilter
  );

  const stagger = (index) =>
    reduceMotion
      ? false
      : {
          initial: { opacity: 0, y: 12 },
          animate: { opacity: 1, y: 0 },
          transition: {
            duration: 0.45,
            delay: index * 0.06,
            ease: [0.16, 1, 0.3, 1],
          },
        };

  const kpiCards = [
    {
      key: "active",
      label: "Active",
      value: kpis.active.toLocaleString("en-US"),
      icon: <CheckCircleOutlineOutlinedIcon fontSize="small" />,
      accent: "#047857",
      hint: kpis.total ? `${Math.round((kpis.active / kpis.total) * 100)}% of roster` : "Live cycles",
      featured: status === "active" && quickFilter !== "expiring",
    },
    {
      key: "expired",
      label: "Expired",
      value: kpis.expired.toLocaleString("en-US"),
      icon: <EventBusyIcon fontSize="small" />,
      accent: "#D71920",
      hint: "Need renewal",
      featured: status === "pending",
    },
    {
      key: "expiring",
      label: "Expiring soon",
      value: kpis.expiring.toLocaleString("en-US"),
      icon: <AccessTimeIcon fontSize="small" />,
      accent: "#F7931E",
      hint: "Ends within 7 days",
      featured: quickFilter === "expiring",
    },
    {
      key: "outstanding",
      label: "Outstanding",
      value: formatMoney(kpis.outstanding),
      icon: <AccountBalanceWalletIcon fontSize="small" />,
      accent: "#D71920",
      hint: "Member balances",
      featured: quickFilter === "outstanding",
    },
  ];

  const renderActions = (ledger) => (
    <div className="flex items-center justify-end gap-1 shrink-0">
      <Tooltip title="Vouchers">
        <IconButton
          aria-label={`Open vouchers for ${ledger?.member_id?.name || "member"}`}
          onClick={() => openVouchers(ledger?._id)}
          className="min-h-[44px] min-w-[44px]"
        >
          <ConfirmationNumberIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Transactions">
        <IconButton
          aria-label={`Open transactions for ${ledger?.member_id?.name || "member"}`}
          onClick={() => openTransactions(ledger?._id)}
          className="min-h-[44px] min-w-[44px]"
        >
          <ReceiptLongIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </div>
  );

  const renderCard = (ledger) => {
    const paid = Number(ledger?.member_id?.balance) <= 0;
    return (
      <article
        key={ledger?._id}
        className="group rounded-gx border border-gx-border bg-gx-canvas/40 hover:bg-gx-surface hover:border-gx-navy/25 hover:shadow-gx transition overflow-hidden"
      >
        <div className="h-1 bg-gradient-to-r from-[#D71920] to-[#F7931E]" />
        <div className="p-4 space-y-4">
          <div className="flex items-start gap-3">
            <button
              type="button"
              className="flex items-start gap-3 text-left min-w-0 flex-1 cursor-pointer"
              onClick={() => openMember(ledger)}
            >
              <MemberAvatar ledger={ledger} />
              <div className="min-w-0">
                <p className="font-semibold text-gx-ink truncate">
                  {ledger?.member_id?.name || "Unknown member"}
                </p>
                <p className="text-xs text-gx-ink-soft truncate">{memberCode(ledger) || "No ID"}</p>
                <div className="flex flex-wrap gap-1 mt-2">
                  <StatusChip
                    active={!!ledger?.is_active}
                    label={ledger?.is_active ? "Active" : "Expired"}
                  />
                  <StatusChip
                    tone={paid ? "success" : "warning"}
                    label={paid ? "Settled" : "Balance due"}
                  />
                  {ledger?.renewal_frequency ? (
                    <StatusChip tone="navy" label={ledger.renewal_frequency} />
                  ) : null}
                </div>
              </div>
            </button>
            {renderActions(ledger)}
          </div>
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <div className="min-w-0">
              <dt className="text-[11px] font-semibold uppercase tracking-wide text-gx-ink-soft">
                Package
              </dt>
              <dd className="text-gx-ink truncate">{ledger?.package_id?.title || "—"}</dd>
              <dd className="text-xs text-gx-ink-soft truncate">{packageCode(ledger)}</dd>
            </div>
            <div className="min-w-0">
              <dt className="text-[11px] font-semibold uppercase tracking-wide text-gx-ink-soft">
                Balance
              </dt>
              <dd className={`font-semibold tabular-nums ${paid ? "text-gx-ink" : "text-gx-red"}`}>
                {formatMoney(ledger?.member_id?.balance || 0)}
              </dd>
            </div>
            <div className="min-w-0">
              <dt className="text-[11px] font-semibold uppercase tracking-wide text-gx-ink-soft">
                Cycle
              </dt>
              <dd className="text-gx-ink">{formatMaybeDate(ledger?.renewed_date || ledger?.start_date)}</dd>
              <dd className="text-xs text-gx-ink-soft">to {formatMaybeDate(ledger?.end_date)}</dd>
            </div>
            <div className="min-w-0">
              <dt className="text-[11px] font-semibold uppercase tracking-wide text-gx-ink-soft">
                Remaining
              </dt>
              <dd>
                <RemainingMeter ledger={ledger} />
              </dd>
            </div>
          </dl>
        </div>
      </article>
    );
  };

  return (
    <>
      <div className="relative m-5">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-8 left-1/2 h-56 w-[min(100%,920px)] -translate-x-1/2"
          style={{
            background:
              "radial-gradient(ellipse at 50% 0%, rgba(2, 42, 58, 0.07), transparent 70%)",
          }}
        />

        <PageHeader
          title="Subscriptions"
          subtitle="Every membership cycle in one roster — remaining days, outstanding balances, and the next renewal."
          actions={
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                className="gx-cta-secondary min-h-[44px] gap-2"
                onClick={() => setOpenStatsModal(true)}
              >
                <BarChartIcon fontSize="small" aria-hidden="true" />
                Stats
              </button>
              {hasPermission(getSessionUser(), 'reporting:export') && (
              <button
                type="button"
                className="gx-cta min-h-[44px] gap-2"
                onClick={handleDownload}
              >
                <FontAwesomeIcon icon={faDownload} aria-hidden="true" />
                Export
              </button>
              )}
            </div>
          }
        />

        {loading ? (
          <SubscriptionsSkeleton />
        ) : (
          <div className="space-y-5">
            <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
              {kpiCards.map((stat, index) => (
                <motion.div key={stat.key} {...stagger(index)}>
                  <StatCard
                    featured={stat.featured}
                    label={stat.label}
                    value={stat.value}
                    icon={stat.icon}
                    accent={stat.accent}
                    hint={stat.featured ? "Showing this roster" : stat.hint}
                    onClick={() => selectKpi(stat.key)}
                  />
                </motion.div>
              ))}
            </div>

            <section className="gx-glass rounded-gx p-4 md:p-5">
              <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3 mb-4">
                <div>
                  <h2 className="text-sm font-semibold text-gx-ink">Find a membership</h2>
                  <p className="text-xs text-gx-ink-soft mt-0.5">
                    Showing {filtered_ledgers.length.toLocaleString("en-US")} of{" "}
                    {(ledgers || []).length.toLocaleString("en-US")} subscriptions
                  </p>
                </div>
                <div className="flex items-center gap-1 rounded-gx-control border border-gx-border bg-gx-surface p-1 self-start">
                  <button
                    type="button"
                    aria-pressed={layout === "cards"}
                    className={`min-h-[44px] min-w-[44px] rounded-gx-control px-3 text-sm font-semibold cursor-pointer transition ${
                      layout === "cards"
                        ? "bg-gx-navy text-white"
                        : "text-gx-navy hover:bg-gx-canvas"
                    }`}
                    onClick={() => setLayout("cards")}
                  >
                    <ViewModuleIcon fontSize="small" aria-hidden="true" />
                    <span className="sr-only">Card layout</span>
                  </button>
                  <button
                    type="button"
                    aria-pressed={layout === "table"}
                    className={`min-h-[44px] min-w-[44px] rounded-gx-control px-3 text-sm font-semibold cursor-pointer transition hidden md:inline-flex items-center justify-center ${
                      layout === "table"
                        ? "bg-gx-navy text-white"
                        : "text-gx-navy hover:bg-gx-canvas"
                    }`}
                    onClick={() => setLayout("table")}
                  >
                    <ViewListIcon fontSize="small" aria-hidden="true" />
                    <span className="sr-only">Table layout</span>
                  </button>
                </div>
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleFilter();
                }}
                className="space-y-4"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-3">
                  <label className="text-xs font-semibold uppercase tracking-wide text-gx-ink-soft xl:col-span-2">
                    Search
                    <div className="relative mt-1.5">
                      <FontAwesomeIcon
                        icon={faSearch}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-gx-ink-soft"
                        aria-hidden="true"
                      />
                      <input
                        type="search"
                        placeholder="Name, member ID, package, or cycle"
                        value={searchQuery}
                        onChange={(e) => handleSearch(e.target.value)}
                        className={`${controlClass} pl-10`}
                      />
                    </div>
                  </label>
                  <label className="text-xs font-semibold uppercase tracking-wide text-gx-ink-soft">
                    End date from
                    <input
                      type="date"
                      value={fromDate}
                      onChange={(e) => {
                        setFromDate(e.target.value);
                        setPage(0);
                      }}
                      className={`${controlClass} mt-1.5`}
                    />
                  </label>
                  <label className="text-xs font-semibold uppercase tracking-wide text-gx-ink-soft">
                    End date to
                    <input
                      type="date"
                      value={toDate}
                      onChange={(e) => {
                        setToDate(e.target.value);
                        setPage(0);
                      }}
                      disabled={!fromDate}
                      min={fromDate}
                      className={`${controlClass} mt-1.5 ${
                        !fromDate ? "bg-gx-canvas cursor-not-allowed text-gx-ink-soft" : ""
                      }`}
                    />
                  </label>
                  <label className="text-xs font-semibold uppercase tracking-wide text-gx-ink-soft">
                    Status
                    <select
                      value={status}
                      onChange={(e) => {
                        setStatus(e.target.value);
                        if (e.target.value !== "active") {
                          setQuickFilter((prev) => (prev === "expiring" ? "" : prev));
                        }
                        setPage(0);
                      }}
                      className={`${controlClass} mt-1.5`}
                    >
                      <option value="">All statuses</option>
                      <option value="active">Active</option>
                      <option value="pending">Expired</option>
                    </select>
                  </label>
                  <label className="text-xs font-semibold uppercase tracking-wide text-gx-ink-soft">
                    Cycle
                    <select
                      value={subscriptionType}
                      onChange={(e) => {
                        setSubscriptionType(e.target.value);
                        setPage(0);
                      }}
                      className={`${controlClass} mt-1.5`}
                    >
                      <option value="">All cycles</option>
                      <option value="monthly">Monthly</option>
                      <option value="yearly">Yearly</option>
                      <option value="annually">Annually</option>
                      <option value="semi annually">Semi Annually</option>
                      <option value="quarterly">Quarterly</option>
                    </select>
                  </label>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button type="submit" className="gx-cta min-h-[44px]">
                    Apply filters
                  </button>
                  <button
                    type="button"
                    className="gx-cta-secondary min-h-[44px]"
                    onClick={handleClear}
                  >
                    Clear
                  </button>
                </div>
              </form>
            </section>

            {filtered_ledgers.length === 0 ? (
              <div className="bg-gx-surface rounded-gx border border-gx-border shadow-gx">
                <EmptyState
                  title={filtersActive ? "No matching subscriptions" : "No subscriptions yet"}
                  description={
                    filtersActive
                      ? "Try a different name, date range, or clear the current filters."
                      : "Memberships will appear here once they are created for this club."
                  }
                  action={
                    filtersActive ? (
                      <button type="button" className="gx-cta-secondary min-h-[44px]" onClick={handleClear}>
                        Clear filters
                      </button>
                    ) : null
                  }
                />
              </div>
            ) : (
              <div className="space-y-4">
                {layout === "table" ? (
                  <>
                    <TableShell className="hidden md:block">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Member</TableCell>
                            <TableCell>Package</TableCell>
                            <TableCell>Cycle</TableCell>
                            <TableCell>Balance</TableCell>
                            <TableCell>Term</TableCell>
                            <TableCell>Remaining</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {paginatedLedgers.map((ledger) => {
                            const paid = Number(ledger?.member_id?.balance) <= 0;
                            return (
                              <TableRow hover key={ledger?._id}>
                                <TableCell>
                                  <button
                                    type="button"
                                    className="flex items-center gap-3 text-left min-w-0 cursor-pointer"
                                    onClick={() => openMember(ledger)}
                                  >
                                    <MemberAvatar ledger={ledger} />
                                    <span className="min-w-0">
                                      <span className="block font-semibold text-gx-ink truncate">
                                        {ledger?.member_id?.name || "Unknown"}
                                      </span>
                                      <span className="block text-xs text-gx-ink-soft truncate">
                                        {memberCode(ledger) || "—"}
                                      </span>
                                    </span>
                                  </button>
                                </TableCell>
                                <TableCell>
                                  <div className="min-w-0">
                                    <p className="text-sm text-gx-ink truncate">
                                      {ledger?.package_id?.title || "—"}
                                    </p>
                                    <p className="text-xs text-gx-ink-soft truncate">{packageCode(ledger)}</p>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  {ledger?.renewal_frequency ? (
                                    <StatusChip tone="navy" label={ledger.renewal_frequency} />
                                  ) : (
                                    "—"
                                  )}
                                </TableCell>
                                <TableCell>
                                  <span
                                    className={`font-semibold tabular-nums ${
                                      paid ? "text-gx-ink" : "text-gx-red"
                                    }`}
                                  >
                                    {formatMoney(ledger?.member_id?.balance || 0)}
                                  </span>
                                </TableCell>
                                <TableCell>
                                  <p className="text-sm text-gx-ink">
                                    {formatMaybeDate(ledger?.renewed_date || ledger?.start_date)}
                                  </p>
                                  <p className="text-xs text-gx-ink-soft">
                                    to {formatMaybeDate(ledger?.end_date)}
                                  </p>
                                </TableCell>
                                <TableCell>
                                  <RemainingMeter ledger={ledger} />
                                </TableCell>
                                <TableCell>
                                  <StatusChip
                                    active={!!ledger?.is_active}
                                    label={ledger?.is_active ? "Active" : "Expired"}
                                  />
                                </TableCell>
                                <TableCell align="right">{renderActions(ledger)}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableShell>
                    <div className="md:hidden grid grid-cols-1 gap-4">
                      {paginatedLedgers.map(renderCard)}
                    </div>
                  </>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {paginatedLedgers.map(renderCard)}
                  </div>
                )}
                <div className="bg-gx-surface rounded-gx border border-gx-border shadow-gx overflow-x-auto">
                  <Table>
                    <TableFooter>
                      <TableRow>
                        <TablePagination
                          rowsPerPageOptions={[5, 10, 25, { label: "All", value: -1 }]}
                          colSpan={1}
                          count={filtered_ledgers.length}
                          rowsPerPage={rowsPerPage}
                          page={page}
                          slotProps={{
                            select: {
                              inputProps: { "aria-label": "rows per page" },
                              native: true,
                            },
                          }}
                          onPageChange={handleChangePage}
                          onRowsPerPageChange={handleChangeRowsPerPage}
                          ActionsComponent={TablePaginationActions}
                        />
                      </TableRow>
                    </TableFooter>
                  </Table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {isPopupOpen && (
        <Popup
          onClose={() => setIsPopupOpen(false)}
          alertType={isSuccessAlert ? "success" : isFaliureAlert ? "failure" : ""}
          message={isSuccessAlert ? successMessage : isFaliureAlert ? failureMessage : ""}
        />
      )}

      <CustomModal isModal={isDeleteModalOpen} handleModal={toggleDeleteModal} size="md">
        <div className="m-5">
          <h2 className="text-xl font-medium">Delete Ledger?</h2>
          <p className="text-gx-ink-soft mt-3">Are you sure you want to delete this ledger?</p>
          {hasPermission(getSessionUser(), 'company_ledgers:delete') && (
          <button
            className="bg-red-600 p-2 px-5 rounded-lg text-white font-medium hover:bg-red-700 mt-5 float-right mb-5 min-h-[44px] cursor-pointer"
            onClick={deleteLedger}
          >
            Delete
          </button>
          )}
        </div>
      </CustomModal>

      <SubscriptionStatsModal
        filtered_ledgers={filtered_ledgers}
        open={openStatsModal}
        setOpen={setOpenStatsModal}
      />
    </>
  );
}

export default Ledgers;
